import React from "react";
import { ChangeLogEntry, Bom, ReportAssignmentRow, ReportTypeDefinition } from "./types";
import { apiErrorMessage, formatLogTime } from "./logic";
import { RaIcon, RaIconName } from "./RaIcon";

const KIND_LABEL: Record<ChangeLogEntry["kind"], { icon: RaIconName; text: string }> = {
  confirmed: { icon: "check", text: "confirmed the report" },
  draft: { icon: "edit", text: "changed the modules" },
  changed: { icon: "edit", text: "changed a confirmed report" },
};

interface HistoryPanelProps {
  /** null = show every report. */
  reportId: number | null;
  /** Entries from GET projects/<id>/history/ (newest first); undefined while loading. */
  entries: ChangeLogEntry[] | undefined;
  loading: boolean;
  error: unknown;
  rows: ReportAssignmentRow[];
  reportTypes: ReportTypeDefinition[];
  boms: Bom[];
  busy: boolean;
  onUndo: (historyId: number) => void;
  onShowAll: () => void;
  onClose: () => void;
}

/**
 * Slide-in side panel listing who confirmed or changed which report. The
 * latest change of a report can be undone (DELETE history/<id>/); the API only
 * allows that for the person who made it or a superuser.
 */
export const HistoryPanel: React.FC<HistoryPanelProps> = ({
  reportId,
  entries,
  loading,
  error,
  rows,
  reportTypes,
  boms,
  busy,
  onUndo,
  onShowAll,
  onClose,
}) => {
  const labelFor = (id: number) => {
    const r = rows.find((x) => x.id === id);
    const t = r && reportTypes.find((x) => x.key === r.typeKey);
    const b = r && boms.find((x) => x.id === r.bomId);
    return `${t?.name ?? ""} · ${b?.name ?? ""}`;
  };

  const all = entries ?? [];
  const latestByReport = new Map<number, number>();
  all.forEach((e) => {
    // entries are newest first
    if (!latestByReport.has(e.reportId)) latestByReport.set(e.reportId, e.id);
  });
  const list = all.filter((e) => reportId === null || e.reportId === reportId);

  return (
    <aside className="hist open" aria-label="Change history">
      <div className="hh">
        <div>
          <b>Change history</b>
          <span>{reportId === null ? "All reports, all BOMs" : labelFor(reportId)}</span>
        </div>
        <button type="button" className="ib" aria-label="Close" onClick={onClose}>
          <RaIcon name="x" />
        </button>
      </div>
      {reportId !== null && (
        <div className="hf">
          <button type="button" className="lnk3" onClick={onShowAll}>
            Show all reports
          </button>
        </div>
      )}
      <div className="hlist">
        {loading && <div className="hempty">Loading…</div>}
        {!!error && (
          <div className="hempty">{apiErrorMessage(error, "Could not load the history.")}</div>
        )}
        {!loading && !error && list.length === 0 && <div className="hempty">No changes yet.</div>}
        {list.map((e) => {
          const v = KIND_LABEL[e.kind];
          return (
            <div key={e.id} className="he">
              <span className={`hi ${e.kind}`}>
                <RaIcon name={v.icon} />
              </span>
              <div className="hbody">
                <div className="hl">
                  <b>{e.who}</b> {v.text}
                </div>
                <div className="hs">
                  {labelFor(e.reportId)} · {formatLogTime(new Date(e.at))}
                </div>
                {(e.add.length > 0 || e.rem.length > 0) && (
                  <div className="chips">
                    {e.add.map((m) => (
                      <span key={`a${m}`} className="mc add">
                        + {m}
                      </span>
                    ))}
                    {e.rem.map((m) => (
                      <span key={`r${m}`} className="mc rm">
                        − {m}
                      </span>
                    ))}
                  </div>
                )}
                {e.why && <div className="hw">“{e.why}”</div>}
                {latestByReport.get(e.reportId) === e.id && (
                  <button
                    type="button"
                    className="lnk3 hundo"
                    disabled={busy}
                    onClick={() => onUndo(e.id)}
                  >
                    Undo
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </aside>
  );
};
